/*
 * OWL - Aceptación de términos y condiciones antes de pagar (versión Savor).
 * Igual lógica que en el tema Mixto, pero con delegación de eventos y
 * MutationObserver porque Savor re-renderiza el drawer/resumen del carrito
 * (morph de secciones) y destruiría listeners directos.
 * REGLA: sin sessionStorage. La evidencia es un atributo del carrito que NO
 * escribe este script: viaja con el envio de PAGAR, porque la casilla lleva
 * name/value/form (snippets/owl-terms-ack.liquid). Desde 2026-09-21; antes se
 * escribia con fetch aparte y se perdia en ~2% de los pedidos del carrito.
 */
(() => {
  let accepted = false;

  const PENDING_MESSAGE = 'Marca la casilla para continuar';
  const PENDING_CLASS = 'owl-terms-ack--pending';

  // Senal NEUTRA (no roja) para cuando el cajon se abre tras "COMPRAR AHORA":
  // todavia no es un error, es el paso que falta. Ver el bloque del final.
  const READY_CLASS = 'owl-terms-ack--ready';
  let readyUntil = 0;

  const clearReady = () => {
    readyUntil = 0;
    for (const box of document.querySelectorAll(`.${READY_CLASS}`)) box.classList.remove(READY_CLASS);
  };

  const scopeOf = (checkbox) => checkbox.closest('.owl-terms-ack');

  // OWL 2026-09-21: el aviso sale tambien EN el boton de pagar, porque es donde
  // estan el ojo y el pulgar al pulsar. Medido en DEV (sesion Estadisticas) a
  // 375x812: en el cajon el mensaje queda en 642-658 y el boton en 668-720, asi
  // que el dedo que pulsa tapa justo la franja del mensaje. El texto vuelve
  // solo a los 2,2 s, o antes si se marca la casilla.
  const BUTTON_WARNING = 'Acepta los términos';
  const BUTTON_WARNING_MS = 2200;
  const buttonTimers = new WeakMap();
  let messageIds = 0;

  const labelOf = (button) => button.querySelector('.button-text') || button;

  const restoreButton = (button) => {
    const label = labelOf(button);
    clearTimeout(buttonTimers.get(label));
    // Si el morph del carrito ya re-pinto el boton, el atributo desaparecio con
    // el y el texto ya es el del servidor: no hay nada que restaurar.
    if (!label.hasAttribute('data-owl-terms-original')) return;
    label.textContent = label.getAttribute('data-owl-terms-original');
    label.removeAttribute('data-owl-terms-original');
  };

  const restoreButtons = (scope) => {
    if (!scope) return;
    for (const button of scope.querySelectorAll('button[name="checkout"]')) restoreButton(button);
  };

  const flagButton = (button) => {
    const label = labelOf(button);
    if (!label.hasAttribute('data-owl-terms-original')) {
      label.setAttribute('data-owl-terms-original', label.textContent);
    }
    // La flecha apunta a la casilla, que en cajon y pagina esta encima del
    // boton. aria-hidden: un lector de pantalla no necesita oir "flecha arriba".
    const arrow = document.createElement('span');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = ' ↑';
    label.replaceChildren(document.createTextNode(BUTTON_WARNING), arrow);
    clearTimeout(buttonTimers.get(label));
    buttonTimers.set(label, setTimeout(() => restoreButton(button), BUTTON_WARNING_MS));
  };

  const clearPending = (checkbox) => {
    checkbox.removeAttribute('aria-invalid');
    clearReady();
    restoreButtons(checkbox.closest('.cart__ctas'));
    const box = scopeOf(checkbox);
    if (!box || !box.classList.contains(PENDING_CLASS)) return;
    box.classList.remove(PENDING_CLASS);
    const message = box.querySelector('[data-owl-terms-message]');
    if (message) message.textContent = '';
  };

  // Se llama cuando se pulsa PAGAR sin aceptar: lleva la casilla a la vista,
  // la resalta y explica que falta. Reinicia la animacion aunque ya estuviera
  // resaltada, para que un segundo clic vuelva a avisar.
  const flagPending = (checkbox) => {
    const box = scopeOf(checkbox);
    if (!box) {
      checkbox.focus();
      return;
    }
    const message = box.querySelector('[data-owl-terms-message]');
    box.classList.remove(READY_CLASS);
    box.classList.remove(PENDING_CLASS);
    void box.offsetWidth; // fuerza reflow: reinicia la animacion
    box.classList.add(PENDING_CLASS);

    // Accesibilidad: la casilla queda marcada como invalida y enlazada al aviso.
    // El id se genera aqui y no en Liquid porque el snippet se pinta dos veces
    // (cajon y pagina del carrito) y un id fijo saldria duplicado.
    checkbox.setAttribute('aria-invalid', 'true');
    if (message) {
      if (!message.id) message.id = `owl-terms-message-${(messageIds += 1)}`;
      checkbox.setAttribute('aria-describedby', message.id);
      // Vaciar y reescribir en el siguiente tick: role="alert" solo se anuncia
      // cuando el contenido CAMBIA, y un segundo clic escribiria lo mismo. Solo
      // se escribe si sigue pendiente, por si la marcaron en esos 40 ms.
      message.textContent = '';
      setTimeout(() => {
        if (box.classList.contains(PENDING_CLASS)) message.textContent = PENDING_MESSAGE;
      }, 40);
    }

    // OWL 2026-09-21: 'nearest' y no 'center'. Medido en DEV (sesion
    // Estadisticas): en /cart la casilla ya estaba en pantalla (y=646 de 812) y
    // 'center' igual hacia saltar la pagina 259 px, asi que el temblor de 0,4 s
    // ocurria DURANTE el salto y no se percibia. 'nearest' no desplaza nada si
    // la casilla ya se ve.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    box.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });

    // El foco DEBE quedarse en la casilla: es lo que lleva alli a quien navega
    // con teclado o lector de pantalla. Medido en DEV el 2026-09-19: una sola
    // llamada a focus() no aguanta — activeElement acababa en BODY, aunque la
    // casilla si es enfocable. Se reintenta, y se vuelve a consultar el nodo
    // por si el morph del carrito lo reemplazo (misma leccion que applyState).
    const reenfocar = () => {
      const vivo = [...document.querySelectorAll('[data-owl-terms-ack]')]
        .find((el) => el.offsetParent !== null) || checkbox;
      if (document.activeElement !== vivo) vivo.focus({ preventScroll: true });
    };
    reenfocar();
    setTimeout(reenfocar, 150);
    setTimeout(reenfocar, 600);
  };

  const applyState = () => {
    document.querySelectorAll('[data-owl-terms-ack]').forEach((checkbox) => {
      if (checkbox.checked !== accepted) checkbox.checked = accepted;
      // OWL 2026-09-19 (conversion): el boton de pagar ya NO se deshabilita.
      // En movil un boton muerto se lee como "la pagina esta rota" o "no hay
      // stock", y el 95,5% del trafico es movil. El consentimiento sigue siendo
      // obligatorio: lo garantiza el listener de clic en fase de captura de mas
      // abajo, que corta el submit si no esta marcada. El `disabled` del carrito
      // vacio lo sigue poniendo Liquid en el servidor, y no se toca.
      // Efecto lateral bueno: al no escribir ya el atributo disabled, desaparece
      // el riesgo de bucle infinito con el MutationObserver que habia aqui.
      if (accepted) clearPending(checkbox);
    });
  };

  // OWL 2026-09-21: aqui habia un guardado por fetch a /cart/update.js, con cola y
  // reintento. Se quito: la aceptacion viaja ahora con el envio de PAGAR. Lo que
  // decidia el paso era `accepted` en memoria, no que la escritura hubiera
  // terminado, asi que en una red lenta el checkout se creaba sin el atributo.

  document.addEventListener('change', (event) => {
    const checkbox = event.target;
    if (!checkbox || !checkbox.matches || !checkbox.matches('[data-owl-terms-ack]')) return;
    accepted = checkbox.checked;
    applyState();
  });

  // El morph de Savor puede parchear el checkbox en el lugar (sin agregar
  // nodos), así que además del observer se escucha el evento estándar de
  // actualización de carrito y se re-aplica el estado al resolverse.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.promise && typeof event.promise.then === 'function') {
      // El re-render de la seccion del carrito ocurre DESPUES de resolverse
      // la promesa (fetch extra de renderSection): re-aplicar con margen.
      event.promise
        .then(() => {
          setTimeout(applyState, 100);
          setTimeout(applyState, 800);
        })
        .catch(() => {});
    } else {
      setTimeout(applyState, 800);
    }
  });

  // Savor reemplaza o parchea el HTML del resumen al actualizar el carrito.
  // El morph puede modificar atributos en el lugar (sin agregar nodos), asi
  // que se observan tambien los cambios del atributo disabled del boton.
  // Sin riesgo de bucle: applyState solo escribe cuando el valor cambia.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        applyState();
        return;
      }
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (
          (node.matches && (node.matches('[data-owl-terms-ack]') || node.matches('button[name="checkout"]'))) ||
          (node.querySelector && node.querySelector('[data-owl-terms-ack], button[name="checkout"]'))
        ) {
          applyState();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });

  // Garantia dura: aunque el boton quedara habilitado visualmente por un
  // re-render, el clic no pasa sin aceptar los terminos (fase captura, antes
  // que cualquier otro handler; el modal de contraentrega corre despues).
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target && event.target.closest && event.target.closest('button[name="checkout"]');
      if (!button || accepted) return;
      const scope = button.closest('.cart__ctas');
      if (!scope || !scope.querySelector('[data-owl-terms-ack]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      applyState();
      flagPending(scope.querySelector('[data-owl-terms-ack]'));
      flagButton(button);
      // Aviso tactil corto. Android lo respeta; iOS no tiene la API y el ?.
      // evita el error. Va dentro del clic porque el navegador exige un gesto.
      navigator.vibrate?.(60);
    },
    true,
  );

  // ─── "COMPRAR AHORA" propio (OWL 2026-09-21) ──────────────────────────────
  // El "Comprar ya" nativo de Shopify iba al checkout sin pasar por el carrito, y
  // por tanto sin esta casilla: 72 de los 76 pedidos sin aceptacion de los ultimos
  // 250 (17-21 sep) salieron por ahi. blocks/accelerated-checkout.liquid pinta
  // ahora un boton propio con data-owl-buy-now, que pulsa el AGREGAR de su mismo
  // formulario: asi respeta variante, cantidad y stock, que ya gestiona el tema.
  // El cajon se abre solo (auto_open_cart_drawer) y aqui se senala la casilla.
  let buyNowPendingUntil = 0;

  const visibleCheckbox = () =>
    [...document.querySelectorAll('[data-owl-terms-ack]')].find((el) => el.offsetParent !== null);

  // Idempotente: si el re-render del cajon le quito la clase, la vuelve a poner;
  // si sigue puesta, no reinicia la animacion. true = ya no hace falta insistir.
  const signalReady = (scroll) => {
    if (accepted) return true;
    const checkbox = visibleCheckbox();
    const box = checkbox && scopeOf(checkbox);
    if (!box || Date.now() > readyUntil) return false;
    if (!box.classList.contains(READY_CLASS)) box.classList.add(READY_CLASS);
    if (scroll) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      box.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
    }
    return true;
  };

  document.addEventListener('click', (event) => {
    const buyNow = event.target?.closest?.('[data-owl-buy-now]');
    if (!buyNow) return;
    event.preventDefault();
    const addButton = buyNow.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
    if (!addButton || addButton.disabled) return;
    buyNowPendingUntil = Date.now() + 8000;
    addButton.click();
  });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add' || Date.now() > buyNowPendingUntil) return;
    buyNowPendingUntil = 0;
    const done = event.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve({});
    done
      .then((result) => {
        if (result?.detail?.didError) return;
        // El cajon se re-pinta DESPUES de resolverse la promesa, y desde la vista
        // rapida no se abre hasta que se cierra el modal: se reintenta hasta ver la
        // casilla del cajon, y se re-afirma una vez por si el re-pintado llega tarde.
        readyUntil = Date.now() + 4000;
        let intentos = 0;
        const intentar = () => {
          if (signalReady(true)) {
            // Re-afirmar MIENTRAS dure la senal, no una sola vez: el re-pintado del
            // cajon (morph) puede llegar tarde y quitarle la clase. Medido por la sesion
            // Estadisticas el 2026-09-21: desde la vista rapida la senal no estaba a los
            // ~2 s. signalReady solo la vuelve a poner si falta, sin reiniciar el pulso.
            const reafirmar = setInterval(() => signalReady(false), 300);
            setTimeout(() => {
              clearInterval(reafirmar);
              clearReady();
            }, 2400);
            return;
          }
          if ((intentos += 1) < 8) setTimeout(intentar, 350);
        };
        setTimeout(intentar, 500);
      })
      .catch(() => {});
  });

  // Estado inicial: SIEMPRE desmarcado (2026-07-11). Hasta 2026-09-21, ademas, se
  // BORRABA el atributo del carrito al cargar cualquier pagina. Se quito: era lo
  // unico que podia borrar una aceptacion real (aceptar -> checkout -> volver a la
  // tienda, o abrir otra pestana -> el checkout abierto la recogia vacia). No hace
  // falta: cada PAGAR exige marcar otra vez en esa misma vista, y un pedido
  // completado vacia el carrito, asi que una aceptacion vieja no pasa a otra compra.
  applyState();
})();
